import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Droplets, TrendingUp, X } from "lucide-react";

const DISMISS_KEY = "wax_alert_dismissed";

const readDismissed = () => {
    try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY)) || {}; } catch { return {}; }
};

function Notice({ kind, icon: Icon, text, active, onShow, onDismiss }) {
    return (
        <div
            className="tactile-card rounded-md px-4 py-3 flex items-center gap-3 fade-up"
            data-testid={`alert-${kind}`}
        >
            <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4" strokeWidth={1.75} />
            </span>
            <p className="text-sm flex-1 min-w-0" data-testid={`alert-${kind}-text`}>{text}</p>
            <Button
                variant={active ? "secondary" : "ghost"} size="sm"
                className={`rounded-full shrink-0 ${active ? "ring-1 ring-primary/40 text-primary" : ""}`}
                onClick={() => onShow(active ? null : kind)}
                data-testid={`alert-${kind}-show-btn`}
            >
                {active ? "Show all" : "Show them"}
            </Button>
            <button
                onClick={onDismiss}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                title="Hide until next visit"
                data-testid={`alert-${kind}-dismiss-btn`}
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}

export default function AlertsBar({ alerts, activeFilter, onFilter }) {
    const [dismissed, setDismissed] = useState(readDismissed);
    const dismiss = (kind) => {
        const next = { ...dismissed, [kind]: true };
        sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next));
        setDismissed(next);
        if (activeFilter === kind) onFilter(null);
    };

    const cleanCount = alerts?.cleaning_due?.length || 0;
    const riseCount = alerts?.price_rises?.length || 0;
    const showClean = cleanCount > 0 && !dismissed.cleaning;
    const showRise = riseCount > 0 && !dismissed.price;
    if (!showClean && !showRise) return null;

    const plural = (n) => (n === 1 ? "record hasn't" : "records haven't");
    return (
        <section className="grid gap-3 md:grid-cols-2" data-testid="alerts-bar">
            {showClean && (
                <Notice
                    kind="cleaning" icon={Droplets} active={activeFilter === "cleaning"}
                    text={`${cleanCount} ${plural(cleanCount)} been cleaned in ${alerts.cleaning_months} months or more.`}
                    onShow={onFilter} onDismiss={() => dismiss("cleaning")}
                />
            )}
            {showRise && (
                <Notice
                    kind="price" icon={TrendingUp} active={activeFilter === "price"}
                    text={`${riseCount} record${riseCount === 1 ? "" : "s"} rose ${alerts.price_threshold_pct}%+ in Discogs value since the last check. You may want to review your insurance cover.`}
                    onShow={onFilter} onDismiss={() => dismiss("price")}
                />
            )}
        </section>
    );
}
